import {
  AccountInfo,
  createNestablePublicClientApplication,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import { envUtils } from "../utils/envUtils";
import { createLocalUrl } from "../utils/helpers";
import { logger } from "../utils";
import { useTokenStore } from "../hooks";

type PcaLoginResponse = {
  status: "success" | "failure";
  token?: string;
  account?: AccountInfo;
  error?: {
    code: number;
    message: string;
    stackTrace: string;
  };
};

class MsGraphServiceClass {
  private _msal: IPublicClientApplication | undefined = undefined;
  private _isInitialized = false;
  private _initializationInProgress = false;
  private _tokenRequest = {
    scopes: ["openid", "profile", "offline_access", "User.Read", "Calendars.ReadWrite"],
  };
  private _accessToken: string | undefined = undefined;
  private _account: AccountInfo | undefined = undefined;

  constructor() {}

  public get isInitialized() {
    return this._isInitialized;
  }

  public get initializationInProgress() {
    return this._initializationInProgress;
  }

  public get canInitializeClient() {
    return !this._isInitialized && !this._initializationInProgress;
  }

  public isNestedAppAuthSupported() {
    return Office?.context?.requirements?.isSetSupported("NestedAppAuth", "1.1") ?? false;
  }

  public isIdentityApiSupported() {
    return Office?.context?.requirements?.isSetSupported("IdentityAPI", "1.3") ?? false;
  }

  public async initializeClient() {
    try {
      if (this._msal || this._isInitialized) {
        logger.warn("MSAL already initialized");
        return;
      } else if (this._initializationInProgress) {
        logger.warn("MSAL initialization already in progress");
        return;
      }

      /** set initialization progress */
      this._initializationInProgress = true;

      if (this.isNestedAppAuthSupported()) {
        logger.info("NestedAppAuth supported. Starting initialization...");
        await this.initializeNestedClient();
      } else if (this.isIdentityApiSupported()) {
        logger.warn("Nested App not supported. Starting public client initialization...");

        /** reset values from store */
        this._accessToken = useTokenStore.getState().msalToken;
        this._account = useTokenStore.getState().msalAccount;

        /** initialize public client flow */
        const response = await this.initializePublicClient();
        if (response) {
          logger.info("Public client initialized successfully", response);
          this.getAccessToken();
        } else {
          logger.error("Public client initialization failed");
        }
      }

      this._isInitialized = true;
      this._initializationInProgress = false;
    } catch (error) {
      this._initializationInProgress = false;
      throw new Error("Error initializing MSAL client");
    }
  }

  private async initializeNestedClient() {
    if (this._msal) {
      logger.warn("MSAL already initialized");
      return;
    } else if (!this.isNestedAppAuthSupported()) {
      logger.warn("NestedAppAuth not supported");
      return;
    }

    logger.debug("Nested client app initialization started...");

    // Set the msal instance
    this._msal = await createNestablePublicClientApplication({
      auth: {
        clientId: envUtils.MICROSOFT_CLIENT_ID,
        authority: "https://login.microsoftonline.com/common",
      },
      cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false,
      },
      system: {
        loggerOptions: {
          logLevel: LogLevel.Verbose,
          loggerCallback: (level: LogLevel, message: string) => {
            // check to see if we can log this message level
            // if not, return early
            if (
              !envUtils.SHOW_CONSOLE_LOGS.includes(level.toString()) ||
              envUtils.RELEASE_TYPE !== "development"
            ) {
              return;
            }

            // log the message
            switch (level) {
              case LogLevel.Error:
                logger.error(message);
                return;
              case LogLevel.Info:
                logger.info(message);
                return;
              case LogLevel.Verbose:
                logger.debug(message);
                return;
              case LogLevel.Warning:
                logger.warn(message);
                return;
            }
          },
          piiLoggingEnabled: true,
        },
      },
    });

    await this._msal.initialize();

    this._isInitialized = true;
  }

  private async initializePublicClient() {
    return new Promise((resolve, reject) => {
      if (this._msal) {
        logger.warn("MSAL already initialized");
        return;
      } else if (!this.isIdentityApiSupported()) {
        logger.warn("IdentityAPI not supported");
        return;
      }

      this._msal = new PublicClientApplication({
        auth: {
          clientId: envUtils.MICROSOFT_CLIENT_ID,
          authority: "https://login.microsoftonline.com/common",
          redirectUri: `${window.location.origin}/pcalogin.html`,
        },
        cache: {
          cacheLocation: "localStorage",
        },
        system: {
          loggerOptions: {
            logLevel: LogLevel.Verbose,
            loggerCallback: (level: LogLevel, message: string) => {
              // check to see if we can log this message level
              // if not, return early
              if (
                !envUtils.SHOW_CONSOLE_LOGS.includes(level.toString()) ||
                envUtils.RELEASE_TYPE !== "development"
              ) {
                return;
              }

              // log the message
              switch (level) {
                case LogLevel.Error:
                  logger.error(message);
                  return;
                case LogLevel.Info:
                  logger.info(message);
                  return;
                case LogLevel.Verbose:
                  logger.debug(message);
                  return;
                case LogLevel.Warning:
                  logger.warn(message);
                  return;
              }
            },
            piiLoggingEnabled: true,
          },
        },
      });

      this._msal.initialize();

      if (this._account) this._msal.setActiveAccount(this._account);

      let loginDialog: Office.Dialog | undefined;

      const processDialogEvent = (
        arg: { error: number; type: string },
        setState: (x: any) => void,
        displayError: (x: string) => void
      ) => {
        switch (arg.error) {
          case 12002:
            displayError(
              "The dialog box has been directed to a page that it cannot find or load, or the URL syntax is invalid."
            );
            break;
          case 12003:
            displayError(
              "The dialog box has been directed to a URL with the HTTP protocol. HTTPS is required."
            );
            break;
          case 12006:
            // 12006 means that the user closed the dialog instead of waiting for it to close.
            // It is not known if the user completed the login or logout, so assume the user is
            // logged out and revert to the app's starting state. It does no harm for a user to
            // press the login button again even if the user is logged in.
            setState({
              authStatus: "notLoggedIn",
              headerMessage: "Welcome",
            });
            break;
          default:
            displayError("Unknown error in dialog box.");
            break;
        }
      };

      const processLoginMessage = (arg: { message: string; origin: string }) => {
        try {
          // Confirm origin is correct.
          if (arg.origin !== window.location.origin) {
            logger.error("Incorrect origin passed to processLoginMessage.");
            reject({ error: "Incorrect origin passed to processLoginMessage." });
          }

          const responseMsg = JSON.parse(arg.message) as PcaLoginResponse;
          if (responseMsg.status === "success") {
            // We now have a valid access token.
            loginDialog.close();

            // set our state token
            if (responseMsg?.token) {
              this._accessToken = responseMsg.token;
              useTokenStore.getState().setMsalToken(responseMsg.token);
            }

            // set out state account information
            if (responseMsg?.account) {
              this._account = responseMsg.account;
              useTokenStore.getState().setMsalAccount(responseMsg.account);
            }

            logger.debug("Login Complete", responseMsg);
            resolve(responseMsg);
          } else {
            // Something went wrong with authentication or the authorization of the web application.
            loginDialog.close();
            logger.error("Login Error:", responseMsg);
            reject(responseMsg);
          }
        } catch (error) {
          logger.error("Error processing login message:", error);
          reject({ error: "Error processing login message" });
        }
      };

      const processLoginDialogEvent = (arg) => {
        processDialogEvent(
          arg,
          (data) => resolve(data),
          (error) => reject(error)
        );
      };

      Office.context.ui.displayDialogAsync(
        createLocalUrl("pcalogin.html"),
        { height: 60, width: 30, promptBeforeOpen: false },
        (result: Office.AsyncResult<Office.Dialog>) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            logger.error(`${result.error.code} ${result.error.message}`);
          } else {
            loginDialog = result.value;
            loginDialog.addEventHandler(
              Office.EventType.DialogMessageReceived,
              processLoginMessage
            );
            loginDialog.addEventHandler(
              Office.EventType.DialogEventReceived,
              processLoginDialogEvent
            );
          }
        }
      );
    });
  }

  public async getAccessToken(): Promise<string | undefined> {
    if (!this._msal) {
      logger.error("MSAL not initialized");
      await this.initializeClient();
      if (!this._msal) {
        throw new Error("MSAL not initialized");
      }
    } else if (!this.isNestedAppAuthSupported()) {
      return this._accessToken;
    }

    try {
      const userAccount = await this._msal.acquireTokenSilent(this._tokenRequest);
      // set our state token
      if (userAccount?.accessToken) {
        this._accessToken = userAccount.accessToken;
        useTokenStore.getState().setMsalToken(userAccount.accessToken);
      }

      // set out state account information
      if (userAccount?.account) {
        this._account = userAccount.account;
        useTokenStore.getState().setMsalAccount(userAccount.account);
      }
    } catch (error) {
      logger.warn(`[Internal Logger] Unable to acquire token silently: ${error}`);
    }

    if (!this._accessToken || this._accessToken === "") {
      // Acquire token silent failure. Send an interactive request via popup.
      try {
        const userAccount = await this._msal.acquireTokenPopup({
          ...this._tokenRequest,
          prompt: "select_account",
        });
        // set our state token
        if (userAccount?.accessToken) {
          this._accessToken = userAccount.accessToken;
          useTokenStore.getState().setMsalToken(userAccount.accessToken);
        }

        // set out state account information
        if (userAccount?.account) {
          this._account = userAccount.account;
          useTokenStore.getState().setMsalAccount(userAccount.account);
        }
      } catch (popupError) {
        // Acquire token interactive failure.
        logger.warn(`[Internal Logger] Unable to acquire token interactively: ${popupError}`);
      }
    }

    // Log error if both silent and popup requests failed.
    if (!this._accessToken || this._accessToken === "") {
      logger.error(`[Internal Logger] Unable to acquire access token.`);
      return undefined;
    }

    return this._accessToken;
  }

  /**
   * Get the user's profile information from Microsoft Graph API.
   * @returns {any} The user's profile information.
   */
  public async getProfile(): Promise<any> {
    if (!this._accessToken) await this.getAccessToken();

    const response = await fetch(`https://graph.microsoft.com/v1.0/me`, {
      method: "GET",
      headers: {
        Authorization: this._accessToken ? `Bearer ${this._accessToken}` : undefined,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      logger.error("Error fetching profile:", response.statusText);
      throw new Error("Error fetching profile");
    }

    return response.json();
  }

  /**
   * Get the user's calendar contacts from Microsoft Graph API.
   *
   * @returns {Promise<any>} The user's contacts from Microsoft Graph API.
   */
  public async getContacts(): Promise<any> {
    if (!this._accessToken) await this.getAccessToken();

    const response = await fetch(`https://graph.microsoft.com/v1.0/me/people`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this._accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      logger.error("Error fetching contacts:", response.statusText);
      throw new Error("Error fetching contacts");
    }

    return response.json();
  }

  /**
   * Gets the calendar events for the user from Microsoft Graph API.
   *
   * @param {string} id
   * @returns {Promise<any>} The calendar events for the user.
   */
  public async getCalendarEvent(id: string): Promise<any> {
    try {
      if (!this._accessToken) await this.getAccessToken();

      const response = await fetch(`https://graph.microsoft.com/v1.0/me/events/${id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this._accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        logger.error("Error fetching calendar events:", response.statusText);
        throw new Error("Error fetching calendar events");
      }

      return response.json();
    } catch (error) {
      logger.error("Error in getCalendarEvent:", error);
      return undefined;
    }
  }

  /**
   * Get a calendar id based on the event data Microsoft Graph API.
   *
   * @param eventData
   * @returns {string | undefined} The calendar id from the event data.
   */
  public getCalendarIdFromEventData(eventData: any): string | undefined {
    const navigationLink = eventData?.["calendar@odata.navigationLink"];
    if (!navigationLink) return undefined;

    const match = navigationLink.match(/calendars\('([^']+)'\)/);
    const calendarId = match ? match[1] : undefined;

    return calendarId;
  }

  /**
   * Adds a webhook subscription for calendar events in Microsoft Graph API.
   *
   * @param {string} userId - The user ID to associate with the subscription.
   * @param {Date} endDate - The expiration date for the subscription.
   *
   * @returns {Promise<any>} The response from the Microsoft Graph API.
   */
  public async createEventSubscription(userId: string, endDate: Date) {
    try {
      if (!this._accessToken) await this.getAccessToken();

      const response = await fetch(`https://graph.microsoft.com/v1.0/subscriptions`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this._accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          changeType: "updated,deleted",
          notificationUrl: `${envUtils.API_BASE_URL}/webhooks/microsoft/calendar/notification`,
          resource: `me/events?$select=subject,bodyPreview,start,end,attendees`,
          expirationDateTime: endDate,
          clientState: userId,
          includeResourceData: true,
          encryptionCertificate: envUtils.MICROSOFT_ENCRYPTION_CERTIFICATE,
          encryptionCertificateId: envUtils.MICROSOFT_ENCRYPTION_CERTIFICATE_ID,
        }),
      });

      return response.json();
    } catch (error) {
      logger.error(error);
      return undefined;
    }
  }
}

export const msGraphService = new MsGraphServiceClass();
